import { useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  APPLIED_FILTER,
  ClientName,
  SliceStopCountFilter,
  type ViewedForecastProperties,
} from "@hopper-b2b/types";
import {
  AccordionCollection,
  ActionButton,
  ActionLink,
  IconComponent,
  IconName,
  MobilePopoverCard,
  Slot,
} from "@hopper-b2b/ui";
import {
  getEnvVariables,
  hasHTSConnectSupport,
  tenantFlagsEnabled,
  useEnablePricePrediction,
  useEnableHorizontalFareDetailsCard,
  useTenantIcons,
  useUberBridge,
} from "@hopper-b2b/utilities";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import { ClientContext } from "../../../../../../../../App";
import { setRenderLoadingSteps } from "../../../../../../../shop/actions/actions";
import { SortOptionSelection } from "../../../../../../../shop/components/FlightShopHeader/components/SortOptionSelection";
import { useFetchFlights } from "../../../../../../../shop/hooks/useFetchFlights";
import {
  type IShopFilterSelector,
  sortOptionSelector,
} from "../../../../../../../shop/reducer";
import {
  FilterType,
  ModalAccordionId,
} from "../../../../../../../shop/v3/components/HorizontalFilterBar/types";
import {
  setOutboundArrivalTimeRange,
  setOutboundDepartureTimeRange,
  setReturnArrivalTimeRange,
  setReturnDepartureTimeRange,
} from "../../../../../../actions/actions";
import {
  getDestination,
  getIsNoLCC,
  getOrigin,
  getOutboundArrivalTimeRange,
  getOutboundDepartureTimeRange,
  getReturnArrivalTimeRange,
  getReturnDepartureTimeRange,
  getStopsOption,
} from "../../../../../../reducer";
import FlightNumberFilterSelection from "../FlightNumberFilterSelection/";
import {
  AirlineFilterSelection,
  AirportFilterSelection,
  FareClassFilterSelection,
  MaxPriceFilterSelection,
  StopsOptionSelection,
} from "../index";
import { TimeWindowSelection } from "../TimeWindowSelection";
import "./styles.scss";

export interface IMobileFlightShopSearchFilterProps {
  hideAirportFilter?: boolean;
  hideFareClassFilter?: boolean;
  flightShopFilters: IShopFilterSelector;
  filtersOpen?: boolean;
  isReturn?: boolean;
  setFiltersOpen?: (open: boolean) => void;
  setRerunPrediction?: () => void;
  viewedForecastProperties: ViewedForecastProperties;
  selectedFilterType?: FilterType;
  resetAllFilters?: () => void;
  hasActiveFilters?: boolean;
}
export const MobileFlightShopSearchFilter = (
  props: IMobileFlightShopSearchFilterProps
) => {
  const {
    setFiltersOpen,
    filtersOpen,
    isReturn,
    setRerunPrediction,
    viewedForecastProperties,
    selectedFilterType,
    resetAllFilters,
    hasActiveFilters,
  } = props;

  const { t } = useI18nContext();
  const tenantIcons = useTenantIcons();
  const { postEvent } = useUberBridge();

  const [openSearchFilter, setOpenSearchFilter] = useState<boolean>(false);

  const [nonstopFilterSelected, setNonstopFilterSelected] =
    useState<boolean>(false);

  const enablePricePrediction = useEnablePricePrediction();
  const fetchFlights = useFetchFlights();
  const dispatch = useDispatch();

  const handleCloseFilter = useCallback(() => {
    if (nonstopFilterSelected) {
      if (enablePricePrediction && setRerunPrediction) {
        setRerunPrediction();
      }
      dispatch(
        setRenderLoadingSteps(
          [ClientName.UBER, ClientName.NUBANK].includes(
            getEnvVariables("clientName") as ClientName
          )
        )
      );
      fetchFlights();
    }
    if (setFiltersOpen) {
      setFiltersOpen(false);
    } else {
      setOpenSearchFilter(false);
    }
  }, [
    dispatch,
    enablePricePrediction,
    fetchFlights,
    nonstopFilterSelected,
    setFiltersOpen,
    setRerunPrediction,
  ]);

  const tenant = getEnvVariables("clientName");
  const isUberClient = tenant === ClientName.UBER;
  const isHTSConnect = hasHTSConnectSupport();

  // This is paired with the updated sticky filters
  const enableHorizontalFareDetailsCard = useEnableHorizontalFareDetailsCard();

  const featureFlag = useContext(ClientContext).featureFlag;
  const { hideAirportFilter, hideFareClassFilter, flightShopFilters } = props;
  const currSortBy = useSelector(sortOptionSelector);
  const currStops = useSelector(getStopsOption);
  const noLCC = useSelector(getIsNoLCC);

  const appliedFilterTrackingProperties = useMemo(() => {
    return {
      noLCC,
      stops_option: currStops,
      departure_date: viewedForecastProperties?.departure_date,
      return_date: viewedForecastProperties?.return_date,
      destination: viewedForecastProperties?.destination,
      origin: viewedForecastProperties?.origin,
      slice_direction: viewedForecastProperties?.slice_direction,
      trip_type: viewedForecastProperties?.trip_type,
    };
  }, [viewedForecastProperties, noLCC, currStops]);

  const sortOptionCopy = useMemo(() => {
    return {
      [featureFlag?.enableJetBlueFlightSort
        ? "stopAndDepartureTime"
        : "fareScore"]: t("sortOption.recommended"),
      price: t("sortOption.price"),
      departureTime: t("sortOption.depart"),
      arrivalTime: t("sortOption.arrive"),
      stops: t("sortOption.stops"),
      duration: t("sortOption.duration"),
    };
  }, [featureFlag?.enableJetBlueFlightSort, t]);

  const stopsCopy = useMemo(() => {
    return {
      [SliceStopCountFilter.ANY_NUMBER]: t("searchFilter.anyStop"),
      [SliceStopCountFilter.NONE]: t("searchFilter.noStops"),
      [SliceStopCountFilter.ONE_OR_LESS]: t("searchFilter.oneOrFewerStops"),
      [SliceStopCountFilter.TWO_OR_LESS]: t("searchFilter.twoOrFewerStops"),
    };
  }, [t]);

  const currAppliedFilters = useMemo(() => {
    return tenantFlagsEnabled.FlightShopShowFilterNames
      ? {
          currSortByText: sortOptionCopy[currSortBy],
          currStopsText: stopsCopy[currStops],
        }
      : null;
  }, [currSortBy, currStops, sortOptionCopy, stopsCopy]);

  const origin = useSelector(getOrigin);
  const destination = useSelector(getDestination);

  const originDestinationCopy = useMemo(() => {
    if (origin?.label && destination?.label) {
      const originSplit = origin?.label.split(",");
      const originParenthesisSplit =
        originSplit[originSplit.length - 1].split(" (");
      const destinationSplit = destination?.label.split(",");
      const destinationParenthesisSplit =
        destinationSplit[destinationSplit.length - 1].split(" (");
      return {
        origin: `${originSplit[0]} (${
          originParenthesisSplit[originParenthesisSplit.length - 1]
        }`,
        destination: `${destinationSplit[0]} (${
          destinationParenthesisSplit[destinationParenthesisSplit.length - 1]
        }`,
      };
    }
  }, [destination?.label, origin?.label]);

  const outboundDepartureTimeRange = useSelector(getOutboundDepartureTimeRange);
  const outboundOnChangeDepartureTimeRange = useCallback(
    (min: number, max: number) => {
      postEvent(APPLIED_FILTER, appliedFilterTrackingProperties);
      dispatch(setOutboundDepartureTimeRange({ min, max }));
    },
    [dispatch, appliedFilterTrackingProperties, postEvent]
  );
  const outboundArrivalTimeRange = useSelector(getOutboundArrivalTimeRange);
  const outboundOnChangeArrivalTimeRange = useCallback(
    (min: number, max: number) => {
      postEvent(APPLIED_FILTER, appliedFilterTrackingProperties);
      dispatch(setOutboundArrivalTimeRange({ min, max }));
    },
    [dispatch, appliedFilterTrackingProperties, postEvent]
  );

  const returnDepartureTimeRange = useSelector(getReturnDepartureTimeRange);
  const returnOnChangeDepartureTimeRange = useCallback(
    (min: number, max: number) => {
      postEvent(APPLIED_FILTER, appliedFilterTrackingProperties);
      dispatch(setReturnDepartureTimeRange({ min, max }));
    },
    [dispatch, appliedFilterTrackingProperties, postEvent]
  );
  const returnArrivalTimeRange = useSelector(getReturnArrivalTimeRange);

  const returnOnChangeArrivalTimeRange = useCallback(
    (min: number, max: number) => {
      postEvent(APPLIED_FILTER, appliedFilterTrackingProperties);
      dispatch(setReturnArrivalTimeRange({ min, max }));
    },
    [dispatch, appliedFilterTrackingProperties, postEvent]
  );

  const {
    priceMax,
    priceMin,
    airlineOptions,
    airportOptions,
    flightNumbersByAirline,
  } = flightShopFilters;

  const accordionContents = useMemo(() => {
    return [
      // Sort by
      {
        id: FilterType.Sort,
        title: (
          <div className="header-container">
            {t("searchFilter.sortHeader")}{" "}
            {tenantFlagsEnabled.FlightShopShowFilterNames ? (
              <p className="filter-name">
                {currAppliedFilters?.currSortByText}
              </p>
            ) : null}
          </div>
        ),
        body: (
          <div className="mobile-filter-option-selection">
            <SortOptionSelection showDropdownContentOnly={true} />
          </div>
        ),
      },
      // StopsOption
      {
        id: FilterType.Stops,
        title: (
          <div className="header-container">
            {t("searchFilter.stopHeader")}{" "}
            {tenantFlagsEnabled.FlightShopShowFilterNames ? (
              <p className="filter-name">{currAppliedFilters?.currStopsText}</p>
            ) : null}
          </div>
        ),
        body: (
          <div className="mobile-filter-option-selection">
            <StopsOptionSelection
              showDropdownContentOnly={true}
              setNonstopFilterSelected={setNonstopFilterSelected}
              trackingProperties={appliedFilterTrackingProperties}
            />
          </div>
        ),
      },
      // FareClass (conditional)
      ...(!hideFareClassFilter
        ? [
            {
              id: FilterType.Fare,
              title: (
                <div className="header-container">
                  {t("searchFilter.fareClass")}
                </div>
              ),
              body: (
                <div className="mobile-filter-option-selection">
                  <FareClassFilterSelection showDropdownContentOnly={true} />
                </div>
              ),
            },
          ]
        : []),
      // MaxPrice
      {
        id: FilterType.Price,
        title: (
          <div className="header-container">{t("searchFilter.price")}</div>
        ),
        body: (
          <div className="mobile-filter-option-selection">
            <MaxPriceFilterSelection
              showDropdownContentOnly={true}
              maximumPrice={priceMax}
              minimumPrice={priceMin}
              trackingProperties={appliedFilterTrackingProperties}
            />
          </div>
        ),
      },
      // Airline
      {
        id: FilterType.Airline,
        title: (
          <div className="header-container">{t("searchFilter.airline")}</div>
        ),
        body: (
          <div className="mobile-filter-option-selection">
            <AirlineFilterSelection
              showDropdownContentOnly={true}
              allAirlines={airlineOptions}
              trackingProperties={appliedFilterTrackingProperties}
            />
          </div>
        ),
      },
      // Time sections - outbound
      ...(!isReturn
        ? [
            // Departure - tagged with FilterType.Times
            {
              id: FilterType.Times,
              title: (
                <div className="header-container">
                  {t("searchFilter.departureLabelOutbound")}
                </div>
              ),
              body: (
                <TimeWindowSelection
                  onChange={outboundOnChangeDepartureTimeRange}
                  timeRange={outboundDepartureTimeRange}
                  label={t("searchFilter.departureTime", {
                    location: originDestinationCopy?.origin,
                  })}
                />
              ),
            },
            // Arrival
            {
              id: ModalAccordionId.OutboundArrival,
              title: (
                <div className="header-container">
                  {t("searchFilter.arrivalLabelOutbound")}
                </div>
              ),
              body: (
                <TimeWindowSelection
                  onChange={outboundOnChangeArrivalTimeRange}
                  timeRange={outboundArrivalTimeRange}
                  label={t("searchFilter.arrivalTime", {
                    location: originDestinationCopy?.destination,
                  })}
                />
              ),
            },
          ]
        : []),
      // Time sections - return
      ...(isReturn
        ? [
            // Departure - tagged with FilterType.Times
            {
              id: FilterType.Times,
              title: (
                <div className="header-container">
                  {t("searchFilter.departureLabelReturn")}
                </div>
              ),
              body: (
                <div className="mobile-filter-option-selection">
                  <TimeWindowSelection
                    onChange={returnOnChangeDepartureTimeRange}
                    timeRange={returnDepartureTimeRange}
                    label={t("searchFilter.departureTime", {
                      location: originDestinationCopy?.destination,
                    })}
                  />
                </div>
              ),
            },
            // Arrival
            {
              id: ModalAccordionId.ReturnArrival,
              title: (
                <div className="header-container">
                  {t("searchFilter.arrivalLabelReturn")}
                </div>
              ),
              body: (
                <div className="mobile-filter-option-selection">
                  <TimeWindowSelection
                    onChange={returnOnChangeArrivalTimeRange}
                    timeRange={returnArrivalTimeRange}
                    label={t("searchFilter.arrivalTime", {
                      location: originDestinationCopy?.origin,
                    })}
                  />
                </div>
              ),
            },
          ]
        : []),
      // Airport (conditional)
      ...(hideAirportFilter
        ? []
        : [
            {
              id: ModalAccordionId.Airport,
              title: (
                <div className="header-container">
                  {t("searchFilter.airport")}
                </div>
              ),
              body: (
                <div className="mobile-filter-option-selection">
                  <AirportFilterSelection
                    showDropdownContentOnly={true}
                    allAirports={airportOptions}
                    trackingProperties={appliedFilterTrackingProperties}
                  />
                </div>
              ),
            },
          ]),
      // Flight Number
      {
        id: ModalAccordionId.FlightNumber,
        title: (
          <div className="header-container">
            {t("searchFilter.flightNumber")}
          </div>
        ),
        body: (
          <div className="mobile-filter-option-selection">
            <FlightNumberFilterSelection
              showDropdownContentOnly={true}
              allAirlines={airlineOptions}
              flightNumbersByAirline={flightNumbersByAirline}
              trackingProperties={appliedFilterTrackingProperties}
            />
          </div>
        ),
      },
    ];
  }, [
    t,
    currAppliedFilters,
    appliedFilterTrackingProperties,
    hideFareClassFilter,
    priceMax,
    priceMin,
    airlineOptions,
    isReturn,
    outboundDepartureTimeRange,
    outboundArrivalTimeRange,
    returnDepartureTimeRange,
    returnArrivalTimeRange,
    originDestinationCopy,
    hideAirportFilter,
    airportOptions,
    flightNumbersByAirline,
    outboundOnChangeDepartureTimeRange,
    outboundOnChangeArrivalTimeRange,
    returnOnChangeDepartureTimeRange,
    returnOnChangeArrivalTimeRange,
    setNonstopFilterSelected,
  ]);

  const renderHeader = useMemo(() => {
    return isUberClient
      ? {
          headerElement: (
            <Typography className="custom-mobile-search-header">
              {t("searchFilter.sortAndFilter")}
              <ActionLink
                className="filter-close-button"
                content={
                  <IconComponent
                    ariaLabel="Close button icon"
                    className={clsx("close-button-icon")}
                    name={IconName.Close}
                  />
                }
                label="Close"
                onClick={handleCloseFilter}
              />
            </Typography>
          ),
        }
      : isHTSConnect
        ? {
            headerElement: (
              <Typography
                variant="h4"
                className="hts-custom-mobile-search-header"
              >
                {t("searchFilter.sortAndFilter")}
                <ActionLink
                  className="filter-close-button"
                  content={
                    <Slot
                      id="dropdown-icon"
                      name="dropdown-icon-close"
                      className="hts-close-modal"
                      component={
                        <img
                          className="hts-close-modal"
                          src={tenantIcons.close}
                          alt=""
                        />
                      }
                    />
                  }
                  label="Close"
                  onClick={handleCloseFilter}
                />
              </Typography>
            ),
          }
        : {
            topRightButton: (
              <ActionLink
                className="filter-close-button"
                content={
                  <IconComponent
                    ariaLabel="Close button icon"
                    className={clsx("close-button-icon")}
                    name={IconName.Close}
                  />
                }
                label="Close"
                onClick={handleCloseFilter}
              />
            ),
          };
  }, [handleCloseFilter, isUberClient, t, isHTSConnect, tenantIcons.close]);

  return (
    <div className="mobile-flight-shop-search-filter-root">
      <div className="mobile-flight-shop-search-filter-container">
        <MobilePopoverCard
          open={filtersOpen ? filtersOpen : openSearchFilter}
          onClose={handleCloseFilter}
          fullScreen={true}
          className="mobile-flight-shop-search-filter full-screen"
          contentClassName="mobile-flight-shop-search-filter-wrapper"
          bottomButton={
            <ActionButton
              className="mobile-flight-shop-filter-button"
              message={t("searchFilter.apply")}
              onClick={handleCloseFilter}
            />
          }
          {...renderHeader}
        >
          {/* // slot in title and "reset button" */}
          <Slot id="mobile-flight-filter-title" title={t("filters")} />
          <div className="mobile-flight-shop-search-filter-content-container">
            <AccordionCollection
              className="flight-shop-search-filter-accordions-container"
              accordionContents={accordionContents}
              defaultExpandedId={selectedFilterType}
              expandIcon={
                <Slot
                  id="dropdown-icon"
                  name="dropdown-icon-end"
                  className="hts-dropdown-icon-end"
                  component={null}
                />
              }
            />
            {enableHorizontalFareDetailsCard ? (
              <ActionButton
                className="mobile-reset-all-button secondary-button-wrapper"
                buttonClassName="secondary-button"
                onClick={resetAllFilters}
                disabled={!hasActiveFilters}
                message={t("clearAllFilters")}
                defaultStyle="h4r-secondary"
              />
            ) : null}
          </div>
        </MobilePopoverCard>
      </div>
    </div>
  );
};
